import { Component, computed, inject, signal } from '@angular/core';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import { PrintLabelOptions, PrintOptions } from '../models/print-options.model';
import { LabelType } from '../models/label-types.enum';
import { NzCheckboxOption } from 'ng-zorro-antd/checkbox';
import { NzTableModule } from 'ng-zorro-antd/table';
import {
    ContentService,
    LayoutModule,
    NotificationsService,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { ZplPreviewComponent } from './zpl-preview.component';
import {
    PrintLabelSettingsComponent,
    PrintSettings,
    PrintSettingsChangeEvent,
} from './print-label-settings.component';
import { ZplRendererService } from '../services/zpl-renderer.service';
import { saveAs } from 'file-saver';
import { PrintJobService } from '../services/print-job.service';
import { NotificationType, ToastMessage, ToastType } from '@pf/shared-common';
import { tap } from 'rxjs';
import { FormControl } from '@angular/forms';

interface PrintViewOptions {
    parentTitle: string;
    labelsTitle: string;
    labelTypeText: string;
}

@Component({
    selector: 'platform-print-labels',
    imports: [
        LayoutModule,
        SharedUiModule,
        NzTableModule,
        ZplPreviewComponent,
        PrintLabelSettingsComponent,
    ],
    providers: [ContentService],
    templateUrl: './print-labels.component.html',
    styleUrl: './print-labels.component.scss',
})
export class PrintLabelsComponent
    implements TemplateDrawerComponent<PrintOptions>
{
    readonly data: PrintOptions = inject(NZ_DRAWER_DATA);
    private readonly zplRendererService = inject(ZplRendererService);
    private readonly printJobService = inject(PrintJobService);
    private readonly notification = inject(NotificationsService);
    printParentControl = new FormControl<boolean>(false);
    LabelType = LabelType;
    drawerRef = inject(NzDrawerRef);
    printViewOptions = signal<PrintViewOptions | null>(null);
    labelsData = signal<NzCheckboxOption[]>([]);
    checked = false;
    indeterminate = false;
    setOfCheckedId = signal(new Set<string>());
    printSettings = signal<PrintSettings>({
        copies: 1,
        options: {
            printerAddress: '',
            printerUid: '',
        },
    });
    printParent = signal(false);

    selectedLabels = computed(() => {
        const labels = this.data.labels.filter(({ barcode }) =>
            this.setOfCheckedId().has(barcode)
        );
        if (this.printParent() && this.data.parentLabel) {
            return [this.data.parentLabel, ...labels];
        }
        return labels;
    });
    printing = signal(false);
    downloading = signal(false);

    constructor() {
        this.setPrintViewOptions(this.data);
        this.setLabels(this.data.labels);
        this.onAllChecked(true);
    }

    updateCheckedSet(barcode: string, checked: boolean): void {
        if (checked) {
            this.setOfCheckedId.update(set => new Set(set.add(barcode)));
        } else {
            this.setOfCheckedId.update(set => {
                set.delete(barcode);
                return new Set(set);
            });
        }
    }

    refreshCheckedStatus(): void {
        this.checked = this.data.labels.every(({ barcode }) =>
            this.setOfCheckedId().has(barcode)
        );
        this.indeterminate =
            this.data.labels.some(({ barcode }) =>
                this.setOfCheckedId().has(barcode)
            ) && !this.checked;
    }

    onItemChecked(barcode: string, checked: boolean): void {
        this.updateCheckedSet(barcode, checked);
        this.refreshCheckedStatus();
    }

    onAllChecked(checked: boolean): void {
        this.data.labels.forEach(({ barcode }) =>
            this.updateCheckedSet(barcode, checked)
        );
        this.refreshCheckedStatus();
    }

    private setPrintViewOptions(printOptions: PrintOptions) {
        switch (printOptions.labelType) {
            case LabelType.Slot:
                this.printViewOptions.set({
                    parentTitle: 'Zone',
                    labelsTitle: 'Select Slots to Print',
                    labelTypeText: 'slots',
                });
                break;
            case LabelType.HandlingUnit:
                this.printViewOptions.set({
                    parentTitle: 'Parent Unit',
                    labelsTitle: 'Select Units to Print',
                    labelTypeText: 'units',
                });
                break;
            case LabelType.Item:
                this.printViewOptions.set({
                    parentTitle: '',
                    labelsTitle: 'Select Items to Print',
                    labelTypeText: 'items',
                });
                break;
            case LabelType.ShippingLabel:
                this.printViewOptions.set({
                    parentTitle: '',
                    labelsTitle: 'Select Labels to Print',
                    labelTypeText: 'labels',
                });
                break;
            default:
                this.printViewOptions.set(null);
                break;
        }
    }

    private setLabels(labelOptions: PrintLabelOptions[]) {
        this.labelsData.set(
            labelOptions.map(label => ({
                label: label.barcode,
                value: label.barcode,
            }))
        );
    }

    downloadSelectedLabels() {
        const labelsToZip = this.selectedLabels().map(label => ({
            zpl: label.zpl,
            fileName: label.barcode,
        }));

        if (labelsToZip.length === 0) {
            return;
        }

        this.downloading.set(true);
        this.zplRendererService
            .createZip({ labels: labelsToZip })
            .subscribe(result => {
                this.downloading.set(false);
                if (result.ok && result.blob) {
                    saveAs(result.blob, 'labels.zip');
                } else if (result.error) {
                    this.notification.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.error,
                        message: 'Could not create zip file.',
                        duration: 2000,
                    } as ToastMessage);
                }
            });
    }

    printSelectedLabels() {
        const { options, copies } = this.printSettings();
        if (!options.printerAddress && !options.printerUid) {
            this.notification.create({
                type: NotificationType.ToastMessage,
                toastType: ToastType.warning,
                message: 'Please check the printer settings.',
                duration: 2000,
            } as ToastMessage);
            return;
        }

        const labels = this.selectedLabels();

        if (!labels || labels.length === 0) {
            return;
        }

        this.printing.set(true);
        this.printJobService
            .createPrintJob(labels, copies, options)
            .pipe(
                tap(() => {
                    this.printing.set(false);
                })
            )
            .subscribe();
    }

    protected onSettingsChanged($event: PrintSettingsChangeEvent) {
        this.printSettings.set($event);
    }

    protected onPrintParentChange() {
        this.printParent.set(!!this.printParentControl.value);
    }
}
